import classNames from "classnames";

import {
  ComponentStatus,
  Component as ComponentType,
  StatusPageLayoutPreset,
  StatusPageTimeFormat,
} from "@/lib/types/statuspages.ts";
import {isTwoColumnLayout} from "@/pages/statuspages/consumption/utils/layout.ts";

import {hasResponseTimeGraphs} from "../utils/responseTimeGraphs.ts";
import {ComponentItem} from "./ComponentItem";

import styles from "./ComponentList.module.scss";

export interface ComponentListProps {
  components: ComponentType[];
  componentStatusLookup: Record<string, ComponentStatus>;
  componentStatusRank: Record<string, number>;
  showResponseTime: boolean;
  showComponentDescriptions?: boolean;
  timezone?: string;
  timeFormat?: StatusPageTimeFormat;
  componentLinkTo?: (serviceId: number) => string;
  layout?: StatusPageLayoutPreset;
  variant?: "default" | "inspire";
}

export const ComponentList = ({
  components,
  componentStatusLookup,
  componentStatusRank,
  showResponseTime,
  showComponentDescriptions = true,
  timezone,
  timeFormat,
  componentLinkTo,
  layout = StatusPageLayoutPreset.EXPANDED,
  variant = "default",
}: ComponentListProps) => {
  if (!components || components.length === 0) {
    return null;
  }

  const isCompact = layout === StatusPageLayoutPreset.COMPACT;
  const isMinimal = layout === StatusPageLayoutPreset.MINIMAL;
  const isTwoColumn = isTwoColumnLayout(layout);
  // The header only labels the sparkline column, so it earns its space only once a row plots one:
  // a page whose components have no response time data would otherwise carry a header over nothing.
  const showResponseTimeColumn = !isTwoColumn && showResponseTime && hasResponseTimeGraphs(components);

  return (
    <div
      className={classNames(styles.componentList, {
        [styles.compact]: isCompact,
        [styles.minimal]: isMinimal,
        [styles.componentListInspire]: variant === "inspire",
      })}
    >
      <div
        className={classNames(styles.componentListScroller, {
          [styles.compact]: isCompact,
          [styles.minimal]: isMinimal,
          [styles.componentListScrollerInspire]: variant === "inspire",
        })}
      >
        {showResponseTimeColumn && <span className={styles.responseTimeLabel}>Response Time (1h)</span>}
        {components.map((component) => (
          <ComponentItem
            key={component.id}
            component={component}
            componentStatusLookup={componentStatusLookup}
            componentStatusRank={componentStatusRank}
            showResponseTime={!isMinimal && showResponseTime}
            showComponentDescriptions={showComponentDescriptions}
            timezone={timezone}
            timeFormat={timeFormat}
            componentLinkTo={componentLinkTo}
            layout={layout}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
};
